import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSetupPage = () => {
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('male');
  const [activityLevel, setActivityLevel] = useState('moderate');
  const [goalWeight, setGoalWeight] = useState('');
  const [goal, setGoal] = useState('maintain');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { refreshUser, user } = useAuth();
  const navigate = useNavigate();

  // Check if profile already exists
  useEffect(() => {
    const checkProfile = async () => {
      try {
        const response = await axios.get(`${API}/profile`);
        if (response.data) {
          // Profile exists, redirect to dashboard
          navigate('/dashboard');
        }
      } catch (error) {
        // Profile doesn't exist, stay on this page
      }
    };
    checkProfile();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/profile`, {
        height: parseFloat(height),
        weight: parseFloat(weight),
        age: parseInt(age),
        gender,
        activity_level: activityLevel,
        goal_weight: goalWeight ? parseFloat(goalWeight) : null,
        goal
      });
      await refreshUser();
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, rgba(243, 234, 255, 0.8) 0%, rgba(255, 232, 240, 0.8) 25%, rgba(232, 244, 255, 0.8) 50%, rgba(255, 232, 213, 0.8) 75%, rgba(243, 234, 255, 0.8) 100%)' }}>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-8">
          <h1 className="heading-2 mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Complete Your Profile</h1>
          <p className="body-medium mb-8" style={{ color: 'rgb(112, 112, 112)' }}>Tell us about yourself to calculate your daily calorie needs</p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="body-small text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Height (cm)</label>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                  required
                />
              </div>

              <div>
                <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Weight (kg)</label>
                <input
                  type="number"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                  required
                />
              </div>

              <div>
                <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Age</label>
                <input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                  required
                />
              </div>

              <div>
                <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Gender</label>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>

            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Activity Level</label>
              <select
                value={activityLevel}
                onChange={(e) => setActivityLevel(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
              >
                <option value="sedentary">Sedentary (little or no exercise)</option>
                <option value="light">Light (exercise 1-3 days/week)</option>
                <option value="moderate">Moderate (exercise 3-5 days/week)</option>
                <option value="active">Active (exercise 6-7 days/week)</option>
                <option value="very_active">Very Active (physical job or training twice a day)</option>
              </select>
            </div>

            <div>
              <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Goal</label>
              <select
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
              >
                <option value="lose">Lose Weight</option>
                <option value="maintain">Maintain Weight</option>
                <option value="gain">Gain Weight</option>
              </select>
            </div>

            {(goal === 'lose' || goal === 'gain') && (
              <div>
                <label className="body-medium block mb-2" style={{ color: 'rgb(24, 24, 24)' }}>Goal Weight (kg)</label>
                <input
                  type="number"
                  value={goalWeight}
                  onChange={(e) => setGoalWeight(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ borderColor: 'rgba(24, 24, 24, 0.2)' }}
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? 'Saving...' : 'Continue to Dashboard'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileSetupPage;